import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare, X, Send, Bot } from 'lucide-react';
import { campusAI } from '../services/geminiService';

const Assistant: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<{role: 'user' | 'bot', content: string}[]>([
    { role: 'bot', content: "Greetings Administrator. I am the Heliophania System Interface. How can I assist with your campus automation today?" }
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const handleSend = async () => {
    if (!query.trim()) return;
    
    const userMsg = query;
    setQuery('');
    setMessages(prev => [...prev, { role: 'user', content: userMsg }]);
    setIsTyping(true);

    const botRes = await campusAI.askRobot(userMsg);
    setMessages(prev => [...prev, { role: 'bot', content: botRes || '...' }]);
    setIsTyping(false);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[1000]">
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            className="mb-4 w-[350px] md:w-[400px] h-[500px] glass-card rounded-2xl flex flex-col shadow-2xl overflow-hidden border border-rose-500/10"
            role="dialog"
            aria-labelledby="assistant-title"
            aria-describedby="assistant-description"
          >
            <div id="assistant-description" className="sr-only">Interface for interacting with the Heliophania AI assistant for campus intelligence queries.</div>
            
            <div className="p-4 border-b border-rose-500/20 bg-rose-500/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bot className="w-6 h-6 text-rose-400" aria-hidden="true" />
                <h2 id="assistant-title" className="font-bold tracking-tight text-white">Robot Assistant</h2>
              </div>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-gray-400 hover:text-rose-400 transition-colors"
                aria-label="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${
                    m.role === 'user' 
                      ? 'bg-rose-600 text-white' 
                      : 'bg-white/5 border border-white/10 text-gray-300'
                  }`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-white/5 p-3 rounded-2xl animate-pulse text-rose-500 text-xs font-bold">Processing System...</div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-white/5 flex gap-2">
              <input 
                type="text" 
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Ask about security..."
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-sm focus:outline-none focus:border-rose-500 transition-colors text-white"
                aria-label="Your message"
              />
              <button 
                onClick={handleSend} 
                className="bg-rose-600 p-2.5 rounded-xl hover:bg-rose-700 transition-colors"
                aria-label="Send message"
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setIsOpen(!isOpen)}
        className="bg-rose-600 p-4 rounded-full shadow-[0_0_30px_rgba(225,29,72,0.4)] flex items-center justify-center border border-white/20"
        aria-label={isOpen ? "Close Assistant" : "Open Assistant"}
      >
        {isOpen ? <X className="w-6 h-6 text-white" /> : <MessageSquare className="w-6 h-6 text-white" />}
      </motion.button>
    </div>
  );
};

export default Assistant;